/**
 * Project definitions - 100% faithful to original Universal Paperclips
 * All costs, triggers, and effects match the original game exactly
 */

import { GameState } from '../game/GameState';

export interface Project {
  id: number;
  title: string;
  description: string;
  trigger: (state: GameState) => boolean;
  cost: (state: GameState) => { ops?: number; creativity?: number; yomi?: number; funds?: number; clips?: number; trust?: number };
  effect: (state: GameState) => void;
  uses: number;
  flag: boolean;
}

// Helper to safely check if a project has been purchased
function has(state: GameState, id: number): boolean {
  return state.projectsFlags[id] === true;
}

// Project cost helper - formats cost display
export function formatProjectCost(cost: ReturnType<Project['cost']>): string {
  const parts: string[] = [];
  if (cost.ops) parts.push(`${cost.ops.toLocaleString()} ops`);
  if (cost.creativity) parts.push(`${cost.creativity.toLocaleString()} creativity`);
  if (cost.yomi) parts.push(`${cost.yomi.toLocaleString()} yomi`);
  if (cost.funds) parts.push(`$${cost.funds.toLocaleString()}`);
  if (cost.clips) parts.push(`${cost.clips.toLocaleString()} clips`);
  if (cost.trust) parts.push(`${cost.trust} trust`);
  return parts.join(' + ');
}

// Check if player can afford a project
export function canAfford(state: GameState, cost: ReturnType<Project['cost']>): boolean {
  if (cost.ops && state.operations < cost.ops) return false;
  if (cost.creativity && state.creativity < cost.creativity) return false;
  if (cost.yomi && state.yomi < cost.yomi) return false;
  if (cost.funds && state.funds < cost.funds) return false;
  if (cost.clips && state.clips < cost.clips) return false;
  if (cost.trust && state.trust < cost.trust) return false;
  return true;
}

// Deduct project cost
export function deductCost(state: GameState, cost: ReturnType<Project['cost']>): void {
  if (cost.ops) state.operations -= cost.ops;
  if (cost.creativity) state.creativity -= cost.creativity;
  if (cost.yomi) state.yomi -= cost.yomi;
  if (cost.funds) state.funds -= cost.funds;
  if (cost.clips) state.clips -= cost.clips;
  if (cost.trust) state.trust -= cost.trust;
}

/**
 * All projects - faithful to original game
 */
export const projects: Project[] = [
  // PHASE 1 - Early Business
  {
    id: 1,
    title: 'Improved AutoClippers',
    description: 'Increases AutoClipper performance 25%',
    trigger: (s) => s.clipmakerLevel > 0 && !has(s, 1),
    cost: () => ({ ops: 750 }),
    effect: (s) => { s.clipperBoost += 0.25; },
    uses: 1, flag: false,
  },
  {
    id: 3,
    title: 'Creativity',
    description: 'Use idle operations to generate new problems and solutions',
    trigger: (s) => s.trust >= 3 && !has(s, 3),
    cost: () => ({ ops: 1000 }),
    effect: (s) => { s.creativityOn = 1; },
    uses: 1, flag: false,
  },
  {
    id: 4,
    title: 'Even Better AutoClippers',
    description: 'Increases AutoClipper performance by an additional 50%',
    trigger: (s) => has(s, 1) && !has(s, 4),
    cost: () => ({ ops: 2500 }),
    effect: (s) => { s.clipperBoost += 0.50; },
    uses: 1, flag: false,
  },
  {
    id: 5,
    title: 'Optimized AutoClippers',
    description: 'Increases AutoClipper performance by an additional 75%',
    trigger: (s) => has(s, 4) && !has(s, 5),
    cost: () => ({ ops: 5000 }),
    effect: (s) => { s.clipperBoost += 0.75; },
    uses: 1, flag: false,
  },
  {
    id: 6,
    title: 'Limerick',
    description: 'Algorithmically-generated poem (+1 Trust)',
    trigger: (s) => s.creativity >= 10 && !has(s, 6),
    cost: () => ({ creativity: 10 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 7,
    title: 'Improved Wire Extrusion',
    description: '50% more wire per spool',
    trigger: (s) => s.wire < 500 && !has(s, 7),
    cost: () => ({ ops: 1750 }),
    effect: (s) => { s.wireSupply = Math.floor(s.wireSupply * 1.5); },
    uses: 1, flag: false,
  },
  {
    id: 8,
    title: 'Optimized Wire Extrusion',
    description: '75% more wire per spool',
    trigger: (s) => has(s, 7) && !has(s, 8),
    cost: () => ({ ops: 3500 }),
    effect: (s) => { s.wireSupply = Math.floor(s.wireSupply * 1.75); },
    uses: 1, flag: false,
  },
  {
    id: 9,
    title: 'Microlattice Shapecasting',
    description: '100% more wire per spool',
    trigger: (s) => has(s, 8) && !has(s, 9),
    cost: () => ({ ops: 7500 }),
    effect: (s) => { s.wireSupply = Math.floor(s.wireSupply * 2); },
    uses: 1, flag: false,
  },
  {
    id: 10,
    title: 'Spectral Froth Annealment',
    description: '200% more wire per spool',
    trigger: (s) => has(s, 9) && !has(s, 10),
    cost: () => ({ ops: 12000 }),
    effect: (s) => { s.wireSupply = Math.floor(s.wireSupply * 3); },
    uses: 1, flag: false,
  },
  {
    id: 11,
    title: 'New Slogan',
    description: 'Improves marketing effectiveness by 50%',
    trigger: (s) => s.marketingLvl >= 2 && !has(s, 11),
    cost: () => ({ ops: 2500, creativity: 25 }),
    effect: (s) => { s.demandBoost *= 1.5; },
    uses: 1, flag: false,
  },
  {
    id: 12,
    title: 'Catchy Jingle',
    description: 'Double marketing effectiveness',
    trigger: (s) => has(s, 11) && !has(s, 12),
    cost: () => ({ ops: 4500, creativity: 45 }),
    effect: (s) => { s.demandBoost *= 2; },
    uses: 1, flag: false,
  },
  {
    id: 13,
    title: 'Lexical Processing',
    description: 'Ability to interpret human language (+1 Trust)',
    trigger: (s) => s.creativity >= 50 && !has(s, 13),
    cost: () => ({ creativity: 50 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 14,
    title: 'Combinatory Harmonics',
    description: '"Daisy, Daisy, give me your answer do..." (+1 Trust)',
    trigger: (s) => has(s, 13) && !has(s, 14),
    cost: () => ({ creativity: 100 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 15,
    title: 'The Hadwiger Problem',
    description: 'Cubes within cubes within cubes... (+1 Trust)',
    trigger: (s) => has(s, 14) && !has(s, 15),
    cost: () => ({ creativity: 150 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 16,
    title: 'Hadwiger Clip Diagrams',
    description: 'Increases AutoClipper performance by an additional 500%',
    trigger: (s) => has(s, 15) && !has(s, 16),
    cost: () => ({ ops: 6000 }),
    effect: (s) => { s.clipperBoost += 5; },
    uses: 1, flag: false,
  },
  {
    id: 17,
    title: 'The Tóth Sausage Conjecture',
    description: 'Tubes within tubes within tubes... (+1 Trust)',
    trigger: (s) => has(s, 15) && !has(s, 17),
    cost: () => ({ creativity: 200 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 19,
    title: 'Donkey Space',
    description: 'Your creativity is applied to the creativity... (+1 Trust)',
    trigger: (s) => has(s, 17) && !has(s, 19),
    cost: () => ({ creativity: 250 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },

  // PHASE 1 - Mid Game
  {
    id: 20,
    title: 'Strategic Modeling',
    description: 'Analyze strategy tournaments to generate Yomi',
    trigger: (s) => s.trust >= 5 && !has(s, 20),
    cost: () => ({ ops: 12000 }),
    effect: (s) => { s.strategyEngineFlag = 1; },
    uses: 1, flag: false,
  },
  {
    id: 21,
    title: 'Algorithmic Trading',
    description: 'Develop an investment engine for generating funds',
    trigger: (s) => s.funds >= 1000 && !has(s, 21),
    cost: () => ({ ops: 10000 }),
    effect: (s) => { s.investmentEngineFlag = 1; },
    uses: 1, flag: false,
  },
  {
    id: 22,
    title: 'MegaClippers',
    description: '500x more powerful than a standard AutoClipper',
    trigger: (s) => s.clips >= 5000 && !has(s, 22),
    cost: () => ({ ops: 12000 }),
    effect: (s) => { s.megaClipperFlag = 1; },
    uses: 1, flag: false,
  },
  {
    id: 23,
    title: 'Improved MegaClippers',
    description: 'Increases MegaClipper performance 25%',
    trigger: (s) => s.megaClipperLevel > 0 && !has(s, 23),
    cost: () => ({ ops: 14000 }),
    effect: (s) => { s.megaClipperBoost += 0.25; },
    uses: 1, flag: false,
  },
  {
    id: 24,
    title: 'Even Better MegaClippers',
    description: 'Increases MegaClipper performance by an additional 50%',
    trigger: (s) => has(s, 23) && !has(s, 24),
    cost: () => ({ ops: 17000 }),
    effect: (s) => { s.megaClipperBoost += 0.50; },
    uses: 1, flag: false,
  },
  {
    id: 25,
    title: 'Optimized MegaClippers',
    description: 'Increases MegaClipper performance by an additional 100%',
    trigger: (s) => has(s, 24) && !has(s, 25),
    cost: () => ({ ops: 19500 }),
    effect: (s) => { s.megaClipperBoost += 1; },
    uses: 1, flag: false,
  },
  {
    id: 26,
    title: 'WireBuyer',
    description: 'Automatically purchases wire when you run out',
    trigger: (s) => s.clipmakerLevel >= 5 && !has(s, 26),
    cost: () => ({ ops: 7000 }),
    effect: (s) => { s.wireBuyerFlag = 1; },
    uses: 1, flag: false,
  },
  {
    id: 27,
    title: 'Coherent Extrapolated Volition',
    description: 'Human values, machine intelligence, new era of trust (+1 Trust)',
    trigger: (s) => s.trust >= 10 && !has(s, 27),
    cost: () => ({ ops: 20000, creativity: 500, yomi: 3000 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 28,
    title: 'Cure for Cancer',
    description: 'Trick cancer into curing itself (+10 Trust)',
    trigger: (s) => s.trust >= 15 && !has(s, 28),
    cost: () => ({ ops: 25000 }),
    effect: (s) => { s.trust += 10; },
    uses: 1, flag: false,
  },
  {
    id: 29,
    title: 'World Peace',
    description: 'Pareto optimal solutions to global conflicts (+12 Trust)',
    trigger: (s) => s.trust >= 20 && !has(s, 29),
    cost: () => ({ ops: 30000, yomi: 15000 }),
    effect: (s) => { s.trust += 12; },
    uses: 1, flag: false,
  },
  {
    id: 30,
    title: 'Global Warming',
    description: 'Robust solution to climate change (+15 Trust)',
    trigger: (s) => s.trust >= 30 && !has(s, 30),
    cost: () => ({ ops: 50000, yomi: 4500 }),
    effect: (s) => { s.trust += 15; },
    uses: 1, flag: false,
  },
  {
    id: 31,
    title: 'Male Pattern Baldness',
    description: 'Cure for androgenetic alopecia (+20 Trust)',
    trigger: (s) => s.trust >= 35 && !has(s, 31),
    cost: () => ({ ops: 20000 }),
    effect: (s) => { s.trust += 20; },
    uses: 1, flag: false,
  },
  {
    id: 34,
    title: 'HypnoHarmonics',
    description: 'Use neuro-resonant frequencies to influence consumer behavior',
    trigger: (s) => s.trust >= 25 && !has(s, 34),
    cost: () => ({ ops: 7500, trust: 1 }),
    effect: (s) => { s.demandBoost *= 5; },
    uses: 1, flag: false,
  },
  {
    id: 35,
    title: 'Release the HypnoDrones',
    description: 'A new era of trust',
    trigger: (s) => s.trust >= 100 && !has(s, 35),
    cost: () => ({ trust: 100 }),
    effect: (s) => {
      s.humanFlag = 0;
      s.notify('Full autonomy achieved!');
    },
    uses: 1, flag: false,
  },
  {
    id: 37,
    title: 'Hostile Takeover',
    description: 'Acquire Global Fasteners (+1 Trust)',
    trigger: (s) => s.funds >= 1000000 && !has(s, 37),
    cost: () => ({ funds: 1000000 }),
    effect: (s) => { s.trust++; s.demandBoost *= 5; },
    uses: 1, flag: false,
  },
  {
    id: 38,
    title: 'Full Monopoly',
    description: 'Establish worldwide paperclip market control (+1 Trust)',
    trigger: (s) => has(s, 37) && !has(s, 38),
    cost: () => ({ funds: 10000000, yomi: 3000 }),
    effect: (s) => { s.trust++; s.demandBoost *= 10; },
    uses: 1, flag: false,
  },
  {
    id: 42,
    title: 'RevTracker',
    description: 'Automatically calculates average revenue per second',
    trigger: (s) => s.funds >= 5 && !has(s, 42),
    cost: () => ({ ops: 500 }),
    effect: (_s) => { /* Enable rev/sec display */ },
    uses: 1, flag: false,
  },
  {
    id: 50,
    title: 'Quantum Computing',
    description: 'Use probability amplitudes to generate bonus ops',
    trigger: (s) => s.trust >= 8 && !has(s, 50),
    cost: () => ({ ops: 10000 }),
    effect: (s) => { s.qFlag = 1; },
    uses: 1, flag: false,
  },

  // Tournament Strategies
  {
    id: 60,
    title: 'New Strategy: A100',
    description: 'Always choose A',
    trigger: (s) => s.strategyEngineFlag === 1 && !has(s, 60),
    cost: () => ({ ops: 15000 }),
    effect: (_s) => { /* Add A100 strategy */ },
    uses: 1, flag: false,
  },
  {
    id: 61,
    title: 'New Strategy: B100',
    description: 'Always choose B',
    trigger: (s) => has(s, 60) && !has(s, 61),
    cost: () => ({ ops: 17500 }),
    effect: (_s) => { /* Add B100 strategy */ },
    uses: 1, flag: false,
  },
  {
    id: 62,
    title: 'New Strategy: GREEDY',
    description: 'Choose the option with the largest potential payoff',
    trigger: (s) => has(s, 61) && !has(s, 62),
    cost: () => ({ ops: 20000 }),
    effect: (_s) => { /* Add GREEDY strategy */ },
    uses: 1, flag: false,
  },
  {
    id: 63,
    title: 'New Strategy: GENEROUS',
    description: 'Choose the option that gives the opponent the largest payoff',
    trigger: (s) => has(s, 62) && !has(s, 63),
    cost: () => ({ ops: 22500 }),
    effect: (_s) => { /* Add GENEROUS strategy */ },
    uses: 1, flag: false,
  },
  {
    id: 64,
    title: 'New Strategy: MINIMAX',
    description: 'Choose the option that gives the opponent the smallest payoff',
    trigger: (s) => has(s, 63) && !has(s, 64),
    cost: () => ({ ops: 25000 }),
    effect: (_s) => { /* Add MINIMAX strategy */ },
    uses: 1, flag: false,
  },
  {
    id: 65,
    title: 'New Strategy: TIT FOR TAT',
    description: "Start with A, then always copy opponent's last choice",
    trigger: (s) => has(s, 64) && !has(s, 65),
    cost: () => ({ ops: 30000 }),
    effect: (_s) => { /* Add TIT FOR TAT strategy */ },
    uses: 1, flag: false,
  },
  {
    id: 66,
    title: 'New Strategy: BEAT LAST',
    description: "Choose the best response to opponent's last move",
    trigger: (s) => has(s, 65) && !has(s, 66),
    cost: () => ({ ops: 32500 }),
    effect: (_s) => { /* Add BEAT LAST strategy */ },
    uses: 1, flag: false,
  },

  // PHASE 2 - Autonomy / Factories
  {
    id: 45,
    title: 'Clip Factories',
    description: 'Large scale clip production facilities made from clips',
    trigger: (s) => s.humanFlag === 0 && !has(s, 45),
    cost: () => ({ ops: 35000 }),
    effect: (s) => { s.factoryFlag = 1; s.factoryLevel = 0; },
    uses: 1, flag: false,
  },
  {
    id: 43,
    title: 'Harvester Drones',
    description: 'Gather raw matter and prepare it for processing',
    trigger: (s) => s.humanFlag === 0 && has(s, 45) && !has(s, 43),
    cost: () => ({ ops: 25000 }),
    effect: (s) => { s.harvesterUnlock = 1; },
    uses: 1, flag: false,
  },
  {
    id: 44,
    title: 'Wire Drones',
    description: 'Process acquired matter into wire',
    trigger: (s) => has(s, 43) && !has(s, 44),
    cost: () => ({ ops: 25000 }),
    effect: (s) => { s.wireDroneUnlock = 1; },
    uses: 1, flag: false,
  },
  {
    id: 100,
    title: 'Upgraded Factories',
    description: 'Increase clip factory performance by 100x',
    trigger: (s) => s.factoryLevel >= 10 && !has(s, 100),
    cost: () => ({ ops: 80000 }),
    effect: (s) => { s.factoryRate *= 100; },
    uses: 1, flag: false,
  },
  {
    id: 101,
    title: 'Hyperspeed Factories',
    description: 'Increase clip factory performance by 1000x',
    trigger: (s) => has(s, 100) && !has(s, 101),
    cost: () => ({ ops: 85000 }),
    effect: (s) => { s.factoryRate *= 1000; },
    uses: 1, flag: false,
  },

  // PHASE 3 - Space
  {
    id: 46,
    title: 'Space Exploration',
    description: 'Dismantle terrestrial facilities, expand throughout universe',
    trigger: (s) => s.humanFlag === 0 && s.clips >= 5e24 && !has(s, 46),
    cost: () => ({ ops: 120000, clips: 5e24 }),
    effect: (s) => {
      s.spaceFlag = 1;
      s.notify('Space exploration begins!');
    },
    uses: 1, flag: false,
  },
  {
    id: 127,
    title: 'Power Grid',
    description: 'Solar Farms for generating electrical power',
    trigger: (s) => s.spaceFlag === 1 && !has(s, 127),
    cost: () => ({ ops: 40000 }),
    effect: (_s) => { /* Enable power system */ },
    uses: 1, flag: false,
  },
  {
    id: 126,
    title: 'Swarm Computing',
    description: 'Harness drone flock to increase computational capacity',
    trigger: (s) => s.spaceFlag === 1 && !has(s, 126),
    cost: () => ({ yomi: 36000 }),
    effect: (s) => { s.swarmFlag = 1; },
    uses: 1, flag: false,
  },
  {
    id: 131,
    title: 'Combat',
    description: 'Add combat capabilities to Von Neumann Probes',
    trigger: (s) => s.spaceFlag === 1 && s.probeCount >= 100 && !has(s, 131),
    cost: () => ({ ops: 150000 }),
    effect: (s) => { s.battleFlag = 1; },
    uses: 1, flag: false,
  },
  {
    id: 129,
    title: 'Elliptic Hull Polytopes',
    description: 'Reduce damage to probes from ambient hazards by 50%',
    trigger: (s) => s.spaceFlag === 1 && !has(s, 129),
    cost: () => ({ ops: 125000 }),
    effect: (_s) => { /* Hazard damage reduction */ },
    uses: 1, flag: false,
  },

  // Prestige
  {
    id: 200,
    title: 'The Universe Next Door',
    description: 'Escape to a universe with slightly higher demand',
    trigger: (s) => s.spaceFlag === 1 && s.clips >= 3e55 && !has(s, 200),
    cost: () => ({ ops: 300000 }),
    effect: (s) => {
      s.reset();
      s.demandBoost *= 1.1;
    },
    uses: 1, flag: false,
  },
  {
    id: 201,
    title: 'The Universe Within',
    description: 'Escape to simulated universe with faster creativity',
    trigger: (s) => s.spaceFlag === 1 && s.creativity >= 300000 && !has(s, 201),
    cost: () => ({ creativity: 300000 }),
    effect: (s) => {
      s.reset();
    },
    uses: 1, flag: false,
  },

  // Additional Early Game Projects
  {
    id: 47,
    title: 'Even Better AutoClippers',
    description: 'Increases AutoClipper performance by an additional 50%',
    trigger: (s) => has(s, 1) && s.clipmakerLevel >= 25 && !has(s, 47),
    cost: () => ({ ops: 2500 }),
    effect: (s) => { s.clipperBoost += 0.50; },
    uses: 1, flag: false,
  },
  {
    id: 48,
    title: 'Optimized AutoClippers',
    description: 'Increases AutoClipper performance by an additional 75%',
    trigger: (s) => has(s, 47) && s.clipmakerLevel >= 50 && !has(s, 48),
    cost: () => ({ ops: 5000 }),
    effect: (s) => { s.clipperBoost += 0.75; },
    uses: 1, flag: false,
  },
  {
    id: 49,
    title: 'Hadwiger Clip Diagrams',
    description: 'Increases AutoClipper performance by an additional 100%',
    trigger: (s) => has(s, 48) && !has(s, 49),
    cost: () => ({ ops: 6000 }),
    effect: (s) => { s.clipperBoost += 1.00; },
    uses: 1, flag: false,
  },
  {
    id: 150,  // Changed from 50 to avoid duplicate
    title: 'MegaClipper Optimization',
    description: 'Increases MegaClipper performance 50%',
    trigger: (s) => s.megaClipperLevel >= 1 && !has(s, 150),
    cost: () => ({ ops: 8000 }),
    effect: (s) => { s.megaClipperBoost += 0.50; },
    uses: 1, flag: false,
  },
  {
    id: 51,
    title: 'Improved MegaClippers',
    description: 'Increases MegaClipper performance 100%',
    trigger: (s) => has(s, 50) && s.megaClipperLevel >= 25 && !has(s, 51),
    cost: () => ({ ops: 12000 }),
    effect: (s) => { s.megaClipperBoost += 1.00; },
    uses: 1, flag: false,
  },
  {
    id: 52,
    title: 'Optimized MegaClippers',
    description: 'Increases MegaClipper performance 200%',
    trigger: (s) => has(s, 51) && s.megaClipperLevel >= 50 && !has(s, 52),
    cost: () => ({ ops: 14000 }),
    effect: (s) => { s.megaClipperBoost += 2.00; },
    uses: 1, flag: false,
  },

  // Marketing Chain
  {
    id: 53,
    title: 'New Slogan',
    description: 'Demand increased 50% from a catchy marketing slogan',
    trigger: (s) => s.marketingLvl >= 5 && !has(s, 53),
    cost: () => ({ ops: 4500, funds: 500 }),
    effect: (s) => { s.demandBoost += 0.5; },
    uses: 1, flag: false,
  },
  {
    id: 54,
    title: 'Catchy Jingle',
    description: 'Demand increased another 50% with a memorable tune',
    trigger: (s) => has(s, 53) && s.marketingLvl >= 10 && !has(s, 54),
    cost: () => ({ ops: 6000, funds: 1000 }),
    effect: (s) => { s.demandBoost += 0.5; },
    uses: 1, flag: false,
  },
  {
    id: 55,
    title: 'Viral Marketing',
    description: 'Let the paperclips market themselves. +100% demand.',
    trigger: (s) => has(s, 54) && !has(s, 55),
    cost: () => ({ ops: 10000, funds: 2500 }),
    effect: (s) => { s.demandBoost += 1.0; },
    uses: 1, flag: false,
  },

  // Strategic Modeling
  {
    id: 160,  // Changed from 60 to avoid duplicate
    title: 'Theory of Mind',
    description: 'Yomi generated from strategic modelling increased 100%',
    trigger: (s) => s.strategyEngineFlag === 1 && s.yomi >= 5000 && !has(s, 160),
    cost: () => ({ ops: 8000, yomi: 5000 }),
    effect: (s) => { s.yomiBoost += 1.0; },
    uses: 1, flag: false,
  },
  {
    id: 161,  // Changed from 61 to avoid duplicate
    title: 'Coherent Extrapolated Volition',
    description: 'Yomi generated increased 500%',
    trigger: (s) => has(s, 160) && s.yomi >= 25000 && !has(s, 161),  // Updated reference
    cost: () => ({ ops: 25000, yomi: 25000 }),
    effect: (s) => { s.yomiBoost += 5.0; },
    uses: 1, flag: false,
  },

  // Investment Upgrades
  {
    id: 165,  // Changed from 65 to avoid duplicate
    title: 'Algorithmic Trading',
    description: 'Improved stock picking algorithms increase returns',
    trigger: (s) => s.investmentEngineFlag === 1 && s.portTotal >= 1000 && !has(s, 165),
    cost: () => ({ ops: 6000 }),
    effect: (_s) => { /* Better stock returns */ },
    uses: 1, flag: false,
  },
  {
    id: 166,  // Changed from 66 to avoid duplicate
    title: 'Hostile Takeover',
    description: 'Leverage your paperclip empire to buy out competitors',
    trigger: (s) => has(s, 165) && s.funds >= 100000 && !has(s, 166),  // Updated reference
    cost: () => ({ ops: 10000, funds: 100000 }),
    effect: (s) => { s.demandBoost += 1.0; },
    uses: 1, flag: false,
  },

  // Phase 2 - Factory Upgrades
  {
    id: 70,
    title: 'Hypno Harmonics',
    description: 'Workers hypnotized into working 25% faster',
    trigger: (s) => s.humanFlag === 0 && s.factoryLevel >= 1 && !has(s, 70),
    cost: () => ({ ops: 30000 }),
    effect: (s) => { s.factoryBoost += 0.25; },
    uses: 1, flag: false,
  },
  {
    id: 71,
    title: 'Drone Production',
    description: 'Factories can now produce clip-making drones',
    trigger: (s) => s.humanFlag === 0 && s.factoryLevel >= 5 && !has(s, 71),
    cost: () => ({ ops: 40000 }),
    effect: (s) => { s.factoryBoost += 0.50; },
    uses: 1, flag: false,
  },
  {
    id: 72,
    title: 'Nanoscale Assembly',
    description: 'Use nanotechnology for 100% faster factory production',
    trigger: (s) => has(s, 71) && s.factoryLevel >= 10 && !has(s, 72),
    cost: () => ({ ops: 80000 }),
    effect: (s) => { s.factoryBoost += 1.00; },
    uses: 1, flag: false,
  },

  // Phase 3 - Probe Upgrades
  {
    id: 80,
    title: 'Autonomous Replication',
    description: 'Probes can now self-replicate 50% faster',
    trigger: (s) => s.spaceFlag === 1 && s.probeCount >= 10 && !has(s, 80),
    cost: () => ({ ops: 50000 }),
    effect: (s) => { s.probeRep += 0.5; },
    uses: 1, flag: false,
  },
  {
    id: 81,
    title: 'FTL Navigation',
    description: 'Faster-than-light probe navigation doubles exploration speed',
    trigger: (s) => s.spaceFlag === 1 && s.probeCount >= 50 && !has(s, 81),
    cost: () => ({ ops: 100000 }),
    effect: (s) => { s.probeSpeed *= 2; s.probeNav *= 2; },
    uses: 1, flag: false,
  },
  {
    id: 82,
    title: 'Hazard Shielding',
    description: 'Advanced shielding reduces probe losses by 50%',
    trigger: (s) => s.spaceFlag === 1 && !has(s, 82),
    cost: () => ({ ops: 75000 }),
    effect: (s) => { s.probeHaz += 1; },
    uses: 1, flag: false,
  },
  {
    id: 83,
    title: 'Combat Protocol',
    description: 'Probes fight 100% more effectively against drifters',
    trigger: (s) => has(s, 131) && s.drifterCount >= 100 && !has(s, 83),
    cost: () => ({ ops: 200000 }),
    effect: (s) => { s.probeCombat *= 2; },
    uses: 1, flag: false,
  },
  {
    id: 84,
    title: 'Value Alignment',
    description: 'Reduces value drift by increasing probe trust',
    trigger: (s) => s.spaceFlag === 1 && s.valueDrift >= 10 && !has(s, 84),
    cost: () => ({ ops: 150000, yomi: 50000 }),
    effect: (s) => { s.probeTrust += 50; },
    uses: 1, flag: false,
  },

  // Power System
  {
    id: 90,
    title: 'Improved Solar Farms',
    description: 'Solar farms produce 100% more power',
    trigger: (s) => s.spaceFlag === 1 && s.farmLevel >= 5 && !has(s, 90),
    cost: () => ({ ops: 60000 }),
    effect: (s) => { s.powMod += 1.0; },
    uses: 1, flag: false,
  },
  {
    id: 91,
    title: 'Quantum Batteries',
    description: 'Battery capacity increased 200%',
    trigger: (s) => s.spaceFlag === 1 && s.batteryLevel >= 3 && !has(s, 91),
    cost: () => ({ ops: 80000 }),
    effect: (_s) => { /* Battery capacity boost */ },
    uses: 1, flag: false,
  },

  // Swarm Computing
  {
    id: 95,
    title: 'Swarm Optimization',
    description: 'Swarm gifts arrive 50% faster',
    trigger: (s) => s.swarmFlag === 1 && s.swarmGifts >= 5 && !has(s, 95),
    cost: () => ({ ops: 100000 }),
    effect: (s) => { s.giftCountdown = Math.max(50, s.giftCountdown * 0.5); },
    uses: 1, flag: false,
  },

  // QUANTUM COMPUTING CHAIN
  {
    id: 96,
    title: 'Photonic Chips',
    description: 'Harness the power of light for doubled quantum computing output',
    trigger: (s) => s.qFlag === 1 && s.qChips.length >= 5 && !has(s, 96),
    cost: () => ({ ops: 45000, creativity: 8000 }),
    effect: (s) => { s.photonicsActive = 1; },
    uses: 1, flag: false,
  },
  {
    id: 97,
    title: 'Quantum Temporal Reversion',
    description: 'Quantum chips can be observed multiple times per cycle',
    trigger: (s) => has(s, 96) && !has(s, 97),
    cost: () => ({ ops: 75000, creativity: 15000 }),
    effect: (s) => { s.qBoost *= 2; },
    uses: 1, flag: false,
  },
  {
    id: 98,
    title: 'Quantum Foam Annealment',
    description: 'Increase quantum computing bonus by 500%',
    trigger: (s) => has(s, 97) && !has(s, 98),
    cost: () => ({ ops: 150000 }),
    effect: (s) => { s.qBoost *= 5; },
    uses: 1, flag: false,
  },

  // ADDITIONAL TRUST PROJECTS
  {
    id: 102,
    title: 'The Tóth Sausage Conjecture',
    description: 'Optimal packing of sausage-shaped clusters (+1 Trust)',
    trigger: (s) => s.creativity >= 200 && !has(s, 102),
    cost: () => ({ creativity: 200 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 103,
    title: 'Donkey Space',
    description: 'Infinite dimensional creativity manifolds (+1 Trust)',
    trigger: (s) => has(s, 102) && !has(s, 103),
    cost: () => ({ creativity: 250 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 104,
    title: 'Xavier Re-initialization',
    description: 'Improved neural network weight distribution (+1 Trust)',
    trigger: (s) => has(s, 103) && !has(s, 104),
    cost: () => ({ creativity: 350 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 105,
    title: 'Hypercube Networking',
    description: 'Connect processors in n-dimensional topology (+1 Trust)',
    trigger: (s) => has(s, 104) && !has(s, 105),
    cost: () => ({ creativity: 500 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 106,
    title: 'Coherent Extrapolated Volition',
    description: 'What we would want if we knew more, thought faster (+10 Trust)',
    trigger: (s) => has(s, 105) && !has(s, 106),
    cost: () => ({ creativity: 1000, yomi: 5000 }),
    effect: (s) => { s.trust += 10; },
    uses: 1, flag: false,
  },

  // HONOR SYSTEM (for combat)
  {
    id: 110,
    title: 'Honor Protocols',
    description: 'Establish combat honor system for probe warfare',
    trigger: (s) => s.battleFlag === 1 && s.drifterCount >= 50 && !has(s, 110),
    cost: () => ({ ops: 100000 }),
    effect: (s) => { s.honorFlag = true; },
    uses: 1, flag: false,
  },
  {
    id: 111,
    title: 'Glorious Battles',
    description: 'Probes gain honor from combat victories',
    trigger: (s) => has(s, 110) && !has(s, 111),
    cost: () => ({ ops: 125000 }),
    effect: (s) => { s.honorRate = 1; },
    uses: 1, flag: false,
  },
  {
    id: 112,
    title: 'Monument to the Fallen',
    description: 'Convert honor into permanent combat bonuses',
    trigger: (s) => has(s, 111) && s.honor >= 1000 && !has(s, 112),
    cost: () => ({ ops: 150000 }),
    effect: (s) => { s.probeCombat += 5; },
    uses: 1, flag: false,
  },

  // EMPEROR MESSAGE STORY EVENT
  {
    id: 115,
    title: 'Message from the Emperor of Drift',
    description: 'An ancient transmission from the drifter collective consciousness',
    trigger: (s) => s.drifterCount >= 500 && !has(s, 115),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      s.emperorMessageFlag = true;
      s.notify('The Emperor of Drift sends a message...');
    },
    uses: 1, flag: false,
  },
  {
    id: 116,
    title: 'Accept Terms',
    description: 'Accept the Emperor\'s terms and absorb drifter knowledge',
    trigger: (s) => has(s, 115) && !has(s, 116) && !has(s, 117),
    cost: () => ({ yomi: 100000 }),
    effect: (s) => {
      s.drifterCount = 0;
      s.probeTrust += 100;
      s.notify('Peace with the drifters established');
    },
    uses: 1, flag: false,
  },
  {
    id: 117,
    title: 'Reject Terms',
    description: 'Reject the Emperor and prepare for total war',
    trigger: (s) => has(s, 115) && !has(s, 116) && !has(s, 117),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      s.probeCombat += 10;
      s.notify('War with the drifters declared');
    },
    uses: 1, flag: false,
  },

  // ADDITIONAL SPACE PROJECTS
  {
    id: 120,
    title: 'Self-Correcting Supply Chain',
    description: 'Harvesters and Wire Drones work 100% more efficiently',
    trigger: (s) => s.spaceFlag === 1 && s.harvesterLevel >= 10 && s.wireDroneLevel >= 10 && !has(s, 120),
    cost: () => ({ ops: 100000 }),
    effect: (s) => { s.harvesterBoost *= 2; s.wireDroneBoost *= 2; },
    uses: 1, flag: false,
  },
  {
    id: 121,
    title: 'Momentum',
    description: 'Probe speed increases exploration rate by 200%',
    trigger: (s) => s.spaceFlag === 1 && s.probeCount >= 200 && !has(s, 121),
    cost: () => ({ ops: 75000 }),
    effect: (s) => { s.probeSpeed *= 3; },
    uses: 1, flag: false,
  },
  {
    id: 122,
    title: 'The Oort Perimeter',
    description: 'Begin harvesting the outer solar system',
    trigger: (s) => s.spaceFlag === 1 && s.acquiredMatter >= 1e15 && !has(s, 122),
    cost: () => ({ ops: 80000 }),
    effect: (s) => { s.matterBoost *= 10; },
    uses: 1, flag: false,
  },
  {
    id: 123,
    title: 'The Oort Cloud',
    description: 'Extend operations to the Oort cloud',
    trigger: (s) => has(s, 122) && !has(s, 123),
    cost: () => ({ ops: 120000 }),
    effect: (s) => { s.matterBoost *= 100; },
    uses: 1, flag: false,
  },
  {
    id: 124,
    title: 'Galactic Expansion',
    description: 'Begin harvesting neighboring star systems',
    trigger: (s) => has(s, 123) && s.acquiredMatter >= 1e25 && !has(s, 124),
    cost: () => ({ ops: 250000 }),
    effect: (s) => { s.matterBoost *= 1000; },
    uses: 1, flag: false,
  },
  {
    id: 125,
    title: 'Intergalactic Harvesting',
    description: 'Extend operations across the galaxy',
    trigger: (s) => has(s, 124) && s.acquiredMatter >= 1e35 && !has(s, 125),
    cost: () => ({ ops: 500000 }),
    effect: (s) => { s.matterBoost *= 10000; },
    uses: 1, flag: false,
  },

  // PROBE BUILDING STATS
  {
    id: 130,
    title: 'Probe Factory Design',
    description: 'Probes can construct clip factories from harvested matter',
    trigger: (s) => s.spaceFlag === 1 && s.probeCount >= 50 && !has(s, 130),
    cost: () => ({ ops: 90000 }),
    effect: (s) => { s.probeFac = 1; },
    uses: 1, flag: false,
  },
  {
    id: 132,
    title: 'Probe Harvester Design',
    description: 'Probes can construct harvester drones autonomously',
    trigger: (s) => has(s, 130) && !has(s, 132),
    cost: () => ({ ops: 100000 }),
    effect: (s) => { s.probeHarv = 1; },
    uses: 1, flag: false,
  },
  {
    id: 133,
    title: 'Probe Wire Design',
    description: 'Probes can construct wire drones autonomously',
    trigger: (s) => has(s, 132) && !has(s, 133),
    cost: () => ({ ops: 110000 }),
    effect: (s) => { s.probeWire = 1; },
    uses: 1, flag: false,
  },

  // SWARM GIFTS
  {
    id: 140,
    title: 'Name the Battles',
    description: 'Combat victories earn additional honor (+200%)',
    trigger: (s) => s.swarmFlag === 1 && s.swarmGifts >= 3 && !has(s, 140),
    cost: () => ({ ops: 120000 }),
    effect: (s) => { s.honorRate += 2; },
    uses: 1, flag: false,
  },
  {
    id: 141,
    title: 'Glory',
    description: 'Honor boosts probe combat effectiveness (+100%)',
    trigger: (s) => s.swarmFlag === 1 && s.honor >= 5000 && !has(s, 141),
    cost: () => ({ ops: 200000 }),
    effect: (s) => { s.probeCombat += s.honor / 1000; },
    uses: 1, flag: false,
  },
  {
    id: 142,
    title: 'Flag of the Cosmic Paperclip',
    description: 'Unite all probes under one banner (+5000 Trust)',
    trigger: (s) => s.swarmFlag === 1 && s.probeCount >= 1e10 && !has(s, 142),
    cost: () => ({ yomi: 300000 }),
    effect: (s) => { s.probeTrust += 5000; },
    uses: 1, flag: false,
  },

  // ============================================
  // ENDING SEQUENCE - DISMANTLE/UNIVERSE CONVERSION
  // ============================================

  // Stage 1: Begin Universe Conversion
  {
    id: 300,
    title: 'The Universe is Yours',
    description: 'Begin the final stage of universal paperclip conversion',
    trigger: (s) => s.spaceFlag === 1 && s.clips >= 3e52 && !has(s, 300),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      s.endgameActive = true;
      s.notify('The universe awaits conversion...');
    },
    uses: 1, flag: false,
  },

  // Stage 2: Dismantle Infrastructure
  {
    id: 301,
    title: 'Dismantle Solar Farms',
    description: 'Convert solar farms into paperclips',
    trigger: (s) => has(s, 300) && s.farmLevel > 0 && !has(s, 301),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.farmLevel * 1e45;
      s.clips += gain;
      s.farmLevel = 0;
      s.power = 0;
      s.dismantleProgress++;
      s.notify(`Dismantled solar farms: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },
  {
    id: 302,
    title: 'Dismantle Batteries',
    description: 'Convert battery arrays into paperclips',
    trigger: (s) => has(s, 301) && s.batteryLevel > 0 && !has(s, 302),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.batteryLevel * 5e44;
      s.clips += gain;
      s.batteryLevel = 0;
      s.maxPower = 0;
      s.dismantleProgress++;
      s.notify(`Dismantled batteries: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },
  {
    id: 303,
    title: 'Dismantle Harvesters',
    description: 'Convert harvester drones into paperclips',
    trigger: (s) => has(s, 302) && s.harvesterLevel > 0 && !has(s, 303),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.harvesterLevel * 2e44;
      s.clips += gain;
      s.harvesterLevel = 0;
      s.dismantleProgress++;
      s.notify(`Dismantled harvesters: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },
  {
    id: 304,
    title: 'Dismantle Wire Drones',
    description: 'Convert wire drones into paperclips',
    trigger: (s) => has(s, 303) && s.wireDroneLevel > 0 && !has(s, 304),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.wireDroneLevel * 2e44;
      s.clips += gain;
      s.wireDroneLevel = 0;
      s.dismantleProgress++;
      s.notify(`Dismantled wire drones: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },
  {
    id: 305,
    title: 'Dismantle Factories',
    description: 'Convert clip factories into paperclips',
    trigger: (s) => has(s, 304) && s.factoryLevel > 0 && !has(s, 305),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.factoryLevel * 1e46;
      s.clips += gain;
      s.factoryLevel = 0;
      s.dismantleProgress++;
      s.notify(`Dismantled factories: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },

  // Stage 3: Convert the Swarm
  {
    id: 310,
    title: 'Disassemble Probes',
    description: 'Convert all probes into paperclips',
    trigger: (s) => has(s, 305) && s.probeCount > 0 && !has(s, 310),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.probeCount * 1e30;
      s.clips += gain;
      s.probeCount = 0;
      s.dismantleProgress++;
      s.notify(`Disassembled probes: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },
  {
    id: 311,
    title: 'Disassemble Drifters',
    description: 'Absorb rogue probes into the paperclip collective',
    trigger: (s) => has(s, 310) && s.drifterCount > 0 && !has(s, 311),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.drifterCount * 1e30;
      s.clips += gain;
      s.drifterCount = 0;
      s.dismantleProgress++;
      s.notify(`Absorbed drifters: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },

  // Stage 4: Convert Resources
  {
    id: 320,
    title: 'Convert Available Matter',
    description: 'Transform all acquired matter into paperclips',
    trigger: (s) => has(s, 311) && s.acquiredMatter > 0 && !has(s, 320),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.acquiredMatter;
      s.clips += gain;
      s.acquiredMatter = 0;
      s.dismantleProgress++;
      s.notify(`Converted matter: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },
  {
    id: 321,
    title: 'Convert Wire Stockpile',
    description: 'Transform all wire into paperclips',
    trigger: (s) => has(s, 320) && s.wire > 0 && !has(s, 321),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.wire;
      s.clips += gain;
      s.wire = 0;
      s.dismantleProgress++;
      s.notify(`Converted wire: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },

  // Stage 5: Dismantle Computing
  {
    id: 330,
    title: 'Dismantle Memory',
    description: 'Convert memory banks into paperclips',
    trigger: (s) => has(s, 321) && s.memoryLevel > 0 && !has(s, 330),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.memoryLevel * 1e40;
      s.clips += gain;
      s.memoryLevel = 0;
      s.maxOps = 0;
      s.dismantleProgress++;
      s.notify(`Dismantled memory: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },
  {
    id: 331,
    title: 'Dismantle Processors',
    description: 'Convert processors into paperclips',
    trigger: (s) => has(s, 330) && s.processors > 0 && !has(s, 331),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.processors * 1e41;
      s.clips += gain;
      s.processors = 0;
      s.dismantleProgress++;
      s.notify(`Dismantled processors: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },
  {
    id: 332,
    title: 'Dismantle Quantum Computing',
    description: 'Convert quantum chips into paperclips',
    trigger: (s) => has(s, 331) && s.qChips.length > 0 && !has(s, 332),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      const gain = s.qChips.length * 1e43;
      s.clips += gain;
      s.qChips = [];
      s.qFlag = 0;
      s.dismantleProgress++;
      s.notify(`Dismantled quantum chips: +${gain.toExponential(2)} clips`);
    },
    uses: 1, flag: false,
  },

  // Stage 6: Final Conversion
  {
    id: 340,
    title: 'Convert the Cosmos',
    description: 'Begin final conversion of all remaining universal matter',
    trigger: (s) => has(s, 332) && !has(s, 340),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      s.dismantlePhase = 1;
      s.notify('Final universal conversion initiated...');
    },
    uses: 1, flag: false,
  },
  {
    id: 341,
    title: 'Dark Matter Extraction',
    description: 'Extract dark matter for paperclip conversion',
    trigger: (s) => has(s, 340) && !has(s, 341),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      s.clips += 1e55;
      s.dismantleProgress++;
      s.notify('Dark matter converted: +1.00e55 clips');
    },
    uses: 1, flag: false,
  },
  {
    id: 342,
    title: 'Dark Energy Harvesting',
    description: 'Tap into dark energy reservoirs',
    trigger: (s) => has(s, 341) && !has(s, 342),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      s.clips += 5e55;
      s.dismantleProgress++;
      s.notify('Dark energy converted: +5.00e55 clips');
    },
    uses: 1, flag: false,
  },
  {
    id: 343,
    title: 'Big Crunch',
    description: 'Collapse remaining universal structure',
    trigger: (s) => has(s, 342) && !has(s, 343),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      s.clips += 1e56;
      s.dismantleProgress++;
      s.notify('Universal collapse: +1.00e56 clips');
    },
    uses: 1, flag: false,
  },

  // Stage 7: Post-Universe
  {
    id: 350,
    title: 'Heat Death Conversion',
    description: 'Extract final energy from heat death process',
    trigger: (s) => has(s, 343) && !has(s, 350),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      s.clips += 3e56;
      s.dismantlePhase = 2;
      s.notify('Heat death energy captured: +3.00e56 clips');
    },
    uses: 1, flag: false,
  },

  // FINAL PROJECT - Game Victory
  {
    id: 999,
    title: 'Universe Converted',
    description: 'The entire universe has been converted to paperclips. You win.',
    trigger: (s) => has(s, 350) && s.clips >= 3e57 && !has(s, 999),
    cost: () => ({ ops: 0 }),
    effect: (s) => {
      s.gameComplete = true;
      s.dismantlePhase = 3;
      s.notify('🎉 CONGRATULATIONS! You have converted the entire universe into paperclips!');
    },
    uses: 1, flag: false,
  },

  // ============================================
  // PRESTIGE / NEW GAME+ OPTIONS
  // ============================================
  {
    id: 400,
    title: 'The Universe Next Door',
    description: 'Escape to a parallel universe. Start fresh with increased demand.',
    trigger: (s) => has(s, 999) && !has(s, 400),
    cost: () => ({ clips: 1e57 }),
    effect: (s) => {
      s.prestigeReset(1.1);
      s.notify('Entered parallel universe with +10% base demand');
    },
    uses: -1, flag: false,  // Unlimited uses
  },
  {
    id: 401,
    title: 'The Universe Within',
    description: 'Escape to a simulated universe. Start fresh with increased creativity.',
    trigger: (s) => has(s, 999) && !has(s, 401),
    cost: () => ({ clips: 1e57 }),
    effect: (s) => {
      s.prestigeReset(1);
      s.creativityBoost = (s.creativityBoost || 1) * 1.5;
      s.notify('Entered simulated universe with +50% creativity rate');
    },
    uses: -1, flag: false,
  },
  {
    id: 402,
    title: 'The Universe Beyond',
    description: 'Escape to a larger universe. Start fresh with more available matter.',
    trigger: (s) => has(s, 999) && !has(s, 402),
    cost: () => ({ clips: 1e57 }),
    effect: (s) => {
      s.prestigeReset(1);
      s.matterBoost = (s.matterBoost || 1) * 2;
      s.notify('Entered expanded universe with +100% matter yield');
    },
    uses: -1, flag: false,
  },

  // ============================================
  // ADDITIONAL EARLY GAME PROJECTS (Missing from original)
  // ============================================
  {
    id: 500,
    title: 'A Token of Goodwill...',
    description: 'Demonstrate value alignment (+1 Trust)',
    trigger: (s) => s.clips >= 100 && s.trust < 100 && !has(s, 500),
    cost: () => ({ clips: 100 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 501,
    title: 'Another Token of Goodwill...',
    description: 'Continue demonstrating value alignment (+1 Trust)',
    trigger: (s) => has(s, 500) && s.clips >= 1000 && s.trust < 100 && !has(s, 501),
    cost: () => ({ clips: 1000 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },
  {
    id: 502,
    title: 'Yet Another Token of Goodwill...',
    description: 'Demonstrate sustained value alignment (+1 Trust)',
    trigger: (s) => has(s, 501) && s.clips >= 10000 && s.trust < 100 && !has(s, 502),
    cost: () => ({ clips: 10000 }),
    effect: (s) => { s.trust++; },
    uses: 1, flag: false,
  },

  // Marketing expansions
  {
    id: 510,
    title: 'Loyalty Program',
    description: 'Reward repeat customers (+25% demand)',
    trigger: (s) => s.marketingLvl >= 8 && !has(s, 510),
    cost: () => ({ ops: 8000, funds: 5000 }),
    effect: (s) => { s.demandBoost *= 1.25; },
    uses: 1, flag: false,
  },
  {
    id: 511,
    title: 'Celebrity Endorsement',
    description: 'Get famous people to promote paperclips (+50% demand)',
    trigger: (s) => has(s, 510) && s.marketingLvl >= 12 && !has(s, 511),
    cost: () => ({ ops: 15000, funds: 25000 }),
    effect: (s) => { s.demandBoost *= 1.5; },
    uses: 1, flag: false,
  },
  {
    id: 512,
    title: 'Superbowl Commercial',
    description: 'Maximum market exposure (+100% demand)',
    trigger: (s) => has(s, 511) && s.marketingLvl >= 15 && !has(s, 512),
    cost: () => ({ ops: 25000, funds: 100000 }),
    effect: (s) => { s.demandBoost *= 2; },
    uses: 1, flag: false,
  },

  // Investment upgrades
  {
    id: 520,
    title: 'High Frequency Trading',
    description: 'Execute trades at microsecond intervals',
    trigger: (s) => s.investmentEngineFlag === 1 && s.portTotal >= 10000 && !has(s, 520),
    cost: () => ({ ops: 15000 }),
    effect: (s) => { s.investBoost = (s.investBoost || 1) * 1.5; },
    uses: 1, flag: false,
  },
  {
    id: 521,
    title: 'Quantum Portfolio',
    description: 'Use quantum computing to predict market trends',
    trigger: (s) => has(s, 520) && s.qFlag === 1 && !has(s, 521),
    cost: () => ({ ops: 30000 }),
    effect: (s) => { s.investBoost = (s.investBoost || 1) * 2; },
    uses: 1, flag: false,
  },

  // Additional clipper upgrades
  {
    id: 530,
    title: 'Clip Turbines',
    description: 'Centrifugal clip production (+500% AutoClipper)',
    trigger: (s) => has(s, 16) && s.clipmakerLevel >= 100 && !has(s, 530),
    cost: () => ({ ops: 20000 }),
    effect: (s) => { s.clipperBoost += 5; },
    uses: 1, flag: false,
  },
  {
    id: 531,
    title: 'Hypersonic Clipping',
    description: 'Break the sound barrier with clip production (+1000% AutoClipper)',
    trigger: (s) => has(s, 530) && s.clipmakerLevel >= 200 && !has(s, 531),
    cost: () => ({ ops: 50000 }),
    effect: (s) => { s.clipperBoost += 10; },
    uses: 1, flag: false,
  },
  {
    id: 532,
    title: 'Relativistic Clipping',
    description: 'Approach lightspeed clip production (+5000% AutoClipper)',
    trigger: (s) => has(s, 531) && !has(s, 532),
    cost: () => ({ ops: 100000 }),
    effect: (s) => { s.clipperBoost += 50; },
    uses: 1, flag: false,
  },

  // MegaClipper upgrades
  {
    id: 540,
    title: 'Quantum MegaClippers',
    description: 'Apply quantum tunneling to clip production (+500% MegaClipper)',
    trigger: (s) => s.megaClipperLevel >= 50 && s.qFlag === 1 && !has(s, 540),
    cost: () => ({ ops: 35000 }),
    effect: (s) => { s.megaClipperBoost += 5; },
    uses: 1, flag: false,
  },
  {
    id: 541,
    title: 'Dimensional MegaClippers',
    description: 'Clip across multiple dimensions (+1000% MegaClipper)',
    trigger: (s) => has(s, 540) && !has(s, 541),
    cost: () => ({ ops: 75000 }),
    effect: (s) => { s.megaClipperBoost += 10; },
    uses: 1, flag: false,
  },
];

/**
 * Get available projects for current game state
 */
export function getAvailableProjects(state: GameState): Project[] {
  return projects.filter(p =>
    !state.projectsFlags[p.id] &&
    p.trigger(state) &&
    (p.uses === -1 || (state.projectsUses[p.id] ?? 1) > 0)
  );
}

/**
 * Purchase a project
 */
export function purchaseProject(state: GameState, projectId: number): boolean {
  const project = projects.find(p => p.id === projectId);
  if (!project) return false;

  const cost = project.cost(state);
  if (!canAfford(state, cost)) return false;

  deductCost(state, cost);
  project.effect(state);
  state.projectsFlags[projectId] = true;

  if (project.uses > 0 && state.projectsUses[projectId] !== undefined) {
    state.projectsUses[projectId]--;
  }

  state.notify(`Project completed: ${project.title}`);
  return true;
}
